import { ref } from 'vue'

/**
 * Composable for handling accessibility features
 * Provides utilities for screen reader announcements and clipboard operations
 */
export const useAccessibility = () => {
  const copyFeedback = ref(false)

  /**
   * Announces a message to screen readers using a live region
   * @param {string} message - The message to announce
   * @param {string} priority - Priority level: 'polite' (default) or 'assertive'
   */
  const announceToScreenReader = (message, priority = 'polite') => {
    const announcement = document.createElement('div')
    announcement.setAttribute('role', 'status')
    announcement.setAttribute('aria-live', priority)
    announcement.setAttribute('aria-atomic', 'true')
    announcement.className = 'sr-only'
    announcement.textContent = message
    document.body.appendChild(announcement)

    // Remove after announcement is read
    setTimeout(() => {
      document.body.removeChild(announcement)
    }, 1000)
  }

  /**
   * Copies text to clipboard with fallback support
   * @param {string} text - The text to copy
   * @returns {Promise<boolean>} - Returns true if copy was successful
   */
  const copyToClipboard = async (text) => {
    try {
      // Try modern Clipboard API first
      await navigator.clipboard.writeText(text)
      copyFeedback.value = true
      announceToScreenReader('Link kopiert')
      resetFeedback()
      return true
    } catch (err) {
      // Fallback for older browsers
      try {
        const textArea = document.createElement('textarea')
        textArea.value = text
        textArea.style.position = 'fixed'
        textArea.style.left = '-999999px'
        document.body.appendChild(textArea)
        textArea.select()
        document.execCommand('copy')
        document.body.removeChild(textArea)

        copyFeedback.value = true
        announceToScreenReader('Link kopiert')
        resetFeedback()
        return true
      } catch (fallbackErr) {
        announceToScreenReader('Fehler beim Kopieren des Links', 'assertive')
        return false
      }
    }
  }

  /**
   * Resets the copy feedback state after a delay
   * @param {number} delay - Delay in milliseconds (default: 2000)
   */
  const resetFeedback = (delay = 2000) => {
    setTimeout(() => {
      copyFeedback.value = false
    }, delay)
  }

  /**
   * Sets focus on an element with optional announcement
   * @param {HTMLElement} element - The element to focus
   * @param {string} announcement - Optional screen reader announcement
   */
  const setFocusWithAnnouncement = (element, announcement = null) => {
    if (element) {
      element.focus()
      if (announcement) {
        announceToScreenReader(announcement)
      }
    }
  }

  /**
   * Validates if screen readers are enabled (limited detection)
   * @returns {boolean} - Best guess if screen reader is active
   */
  const isScreenReaderActive = () => {
    // This is a best-effort detection
    // Check for common screen reader user agents
    const userAgent = navigator.userAgent.toLowerCase()
    return (
      userAgent.includes('nvda') ||
      userAgent.includes('jaws') ||
      userAgent.includes('voiceover')
    )
  }

  /**
   * Registers a handler for the Escape key to support keyboard-based closing
   * @param {Function} callback - Function to execute when Escape is pressed
   * @returns {Function} - Cleanup function to remove the event listener
   */
  const registerEscapeKeyHandler = (callback) => {
    if (typeof document === 'undefined') {
      return () => {}
    }

    const handler = (event) => {
      if (event.key === 'Escape') {
        callback?.(event)
      }
    }

    document.addEventListener('keydown', handler)

    return () => {
      document.removeEventListener('keydown', handler)
    }
  }

  /**
   * Handles key navigation between flyout menu items and subitems
   * @param {KeyboardEvent} event - Keydown event
   * @param {Object} selectors - Optional selectors for focus targets
   * @param {string} selectors.nextTarget - Selector for forward navigation
   * @param {string} selectors.previousTarget - Selector for backward navigation
   */
  const handleFlyoutMenuKeyNavigation = (event, selectors = {}) => {
    if (typeof document === 'undefined') {
      return
    }

    const {
      nextTarget = '.menu-subitem',
      previousTarget = '.menu-item-top.active'
    } = selectors

    if (event.key === 'ArrowRight') {
      event.preventDefault()
      const firstSubitem = document.querySelector(nextTarget)
      firstSubitem?.focus()
    }

    if (event.key === 'ArrowLeft') {
      event.preventDefault()
      const activeButton = document.querySelector(previousTarget)
      activeButton?.focus()
    }
  }

  return {
    copyFeedback,
    announceToScreenReader,
    copyToClipboard,
    resetFeedback,
    setFocusWithAnnouncement,
    isScreenReaderActive,
    registerEscapeKeyHandler,
    handleFlyoutMenuKeyNavigation
  }
}
